import {useEffect, useRef, useState} from 'react';
import {create} from '@bufbuild/protobuf';
import {useConnectClients} from '../grpc/ConnectClientContext';
import {StreamTournamentUpdatesRequestSchema, StreamTournamentUpdatesTournamentInfo} from '../../generated/smg_public_api_pb';

const useTournamentStream = (tournamentId?: string) => {
  const {publicApi} = useConnectClients();
  const [tournament, setTournament] = useState<StreamTournamentUpdatesTournamentInfo | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (tournamentId === undefined) return;

    isMountedRef.current = true;

    const scheduleReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          console.log('[useTournamentStream] Reconnecting in 5s...');
          start();
        }
      }, 5000);
    };

    const start = async () => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      console.log('[useTournamentStream] Starting stream...');

      try {
        const request = create(StreamTournamentUpdatesRequestSchema, { tournamentId: +tournamentId });

        const iterable = publicApi.streamTournamentUpdates(request, { signal: controller.signal });

        for await (const reply of iterable) {
          if (controller.signal.aborted) break;

          const tournamentData = reply.tournament;
          if (tournamentData && isMountedRef.current) {
            setTournament(tournamentData);
            setIsConnected(true);
            setError(null);
          }
        }

        if (!controller.signal.aborted && isMountedRef.current) {
          console.warn('[useTournamentStream] Stream ended.');
          setIsConnected(false);
          scheduleReconnect();
        }

      } catch (e: any) {
        if (controller.signal.aborted) return;

        console.error('[useTournamentStream] Stream error:', e);

        if (isMountedRef.current) {
          setError(e?.message || 'Stream error');
          setIsConnected(false);
          scheduleReconnect();
        }
      }
    };

    start();

    return () => {
      console.log('[useTournamentStream] Cleaning up...');
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [tournamentId]);

  return {tournament, isConnected, error};
};

export default useTournamentStream;
